import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FieldHeader from "./components/FieldHeader";
import FieldBottomNav from "./components/FieldBottomNav";
import OfflineBanner from "./components/OfflineBanner";
import FieldHome from "./components/FieldHome";
import MobileTreeForm from "./components/MobileTreeForm";
import MobileInspectionForm from "./components/MobileInspectionForm";
import FieldPhotos from "./components/FieldPhotos";
import FieldMap from "./components/FieldMap";
import DraftsList from "./components/DraftsList";
import SyncPage from "./components/SyncPage";
import QrCodeScannerMock from "./components/QrCodeScannerMock";
import MobileTreeSummary from "./components/MobileTreeSummary";
import { projetoAtivo } from "@/mocks/coletor";

type Screen = "home" | "nova-arvore" | "vistoria" | "fotos" | "mapa" | "rascunhos" | "sincronizacao" | "qrcode" | "fca";

const screenToPath: Record<Screen, string> = {
  home: "/coletor",
  "nova-arvore": "/coletor/nova-arvore",
  vistoria: "/coletor/vistoria",
  fotos: "/coletor/fotos",
  mapa: "/coletor/mapa",
  rascunhos: "/coletor/rascunhos",
  sincronizacao: "/coletor/sincronizacao",
  qrcode: "/coletor/qrcode",
  fca: "/coletor/fca",
};

const pathToScreen: Record<string, Screen> = {
  "/coletor": "home",
  "/coletor/nova-arvore": "nova-arvore",
  "/coletor/vistoria": "vistoria",
  "/coletor/fotos": "fotos",
  "/coletor/mapa": "mapa",
  "/coletor/rascunhos": "rascunhos",
  "/coletor/sincronizacao": "sincronizacao",
  "/coletor/qrcode": "qrcode",
  "/coletor/fca": "fca",
};

export default function ColetorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOffline, setIsOffline] = useState(false);
  const [fcaTreeId, setFcaTreeId] = useState("");

  const currentScreen: Screen = pathToScreen[location.pathname] || "home";

  const goTo = useCallback((path: string) => {
    if (path.startsWith("/coletor/fca/")) {
      const id = path.replace("/coletor/fca/", "");
      setFcaTreeId(id);
      navigate("/coletor/fca");
      return;
    }
    navigate(path);
  }, [navigate]);

  const goHome = useCallback(() => goTo("/coletor"), [goTo]);

  useEffect(() => {
    document.title = "FloraIQ — Coletor de Campo";
  }, []);

  // Simular toggle offline
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "o" && e.ctrlKey) {
        e.preventDefault();
        setIsOffline((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const bottomNavItems = useMemo(() => [
    { icon: "ri-home-line", label: "Início", href: "/coletor", active: currentScreen === "home" },
    { icon: "ri-leaf-line", label: "Nova Árvore", href: "/coletor/nova-arvore", active: currentScreen === "nova-arvore" },
    { icon: "ri-clipboard-line", label: "Vistoria", href: "/coletor/vistoria", active: currentScreen === "vistoria" },
    { icon: "ri-map-pin-line", label: "Mapa", href: "/coletor/mapa", active: currentScreen === "mapa" },
    { icon: "ri-cloud-line", label: "Sincronizar", href: "/coletor/sincronizacao", active: currentScreen === "sincronizacao", badge: projetoAtivo.pendentesSincronizacao },
  ], [currentScreen]);

  const connectionStatus = isOffline ? "offline" : "online";

  const handleSaveTree = (data: Record<string, unknown>) => {
    alert("Árvore salva localmente! Código: " + (data.codigo as string));
    goHome();
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <FieldHeader
        projetoNome={projetoAtivo.nome}
        connectionStatus={connectionStatus}
        gpsStatus="capturado"
        precisaoGps={projetoAtivo.precisaoGps}
        pendentes={projetoAtivo.pendentesSincronizacao}
      />
      <OfflineBanner visible={isOffline} />

      <div className="pb-16">
        {currentScreen === "home" && (
          <FieldHome projetoAtivo={projetoAtivo} onNavigate={goTo} />
        )}
        {currentScreen === "nova-arvore" && (
          <MobileTreeForm
            projetoAtivo={{ nome: projetoAtivo.nome, responsavel: projetoAtivo.responsavel }}
            onClose={goHome}
            onSave={handleSaveTree}
          />
        )}
        {currentScreen === "vistoria" && (
          <MobileInspectionForm onClose={goHome} />
        )}
        {currentScreen === "fotos" && (
          <FieldPhotos onClose={goHome} />
        )}
        {currentScreen === "mapa" && (
          <FieldMap onClose={goHome} />
        )}
        {currentScreen === "rascunhos" && (
          <DraftsList onClose={goHome} onEdit={(id) => goHome()} />
        )}
        {currentScreen === "sincronizacao" && (
          <SyncPage onClose={goHome} />
        )}
        {currentScreen === "qrcode" && (
          <QrCodeScannerMock onClose={goHome} />
        )}
        {currentScreen === "fca" && (
          <MobileTreeSummary treeId={fcaTreeId || "CTA-GO-5208707-00426"} onClose={goHome} />
        )}
      </div>

      <FieldBottomNav items={bottomNavItems} onNavigate={goTo} />
    </div>
  );
}